import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Map, List, fromJS } from 'immutable';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import PropTypes from 'prop-types';
import { createContext, useEffect, useState } from 'react';
import { Provider } from 'react-redux';
import axiosMiddleware from 'redux-axios-middleware';
import { createLogger } from 'redux-logger';
import thunk from 'redux-thunk';
import r from 'render-dom';

import MeRecord from 'records/me';
import PlanRecord from 'records/plan';
import createStore from 'redux/createStore';
import { authHttp, http, httpConfig } from 'utils/http';
import { mapMe } from 'utils/mapToRecord';

import suggestionMiddleware from './redux/suggestions/middleware';
import marketDataMiddleware from './redux/marketData/middleware';
import muiTheme from './theme';

const propTypes = {
    children: PropTypes.node,
    refreshMe: PropTypes.func,
    user: PropTypes.object,
};

const middleware = [
    axiosMiddleware(http, httpConfig),
    thunk,
    suggestionMiddleware,
    marketDataMiddleware,
];

if (process.env.REACT_APP_ENV === 'development') {
    middleware.push(createLogger());
}

export const store = createStore(middleware);
export const PlanContext = createContext();
export const MeContext = createContext();
export const NotificationContext = createContext();
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_KEY);

function _ProviderWrapper({
    children,
    isFetchingMe,
    me,
    refreshMe,
    plans,
    isFetchingPlans,
    refreshPlans
}) {
    const activeSubscription = me.getIn(['company', 'activeSubscription']) || Map();
    const userPlan = activeSubscription.get('plan') || PlanRecord();
    const [notificationCount, setNotificationCount] = useState(0);
    const [plan, setPlan] = useState(userPlan);
    const elementsOptions = {
        fonts: [{ cssSrc: 'https://use.typekit.net/yje7cer.css' }],
    };

    useEffect(() => {
        if (me.get('isAdmin')) {
            setPlan(Map({
                active: true,
                allowExclusiveCampaigns: true,
                boosts: -1,
                campaigns: 100,
                chat: true,
                deepWords: -1,
                followups: true,
                keywordDepth: 5,
                keywords: 100,
                minimumRebatePercentage: 20,
                monitorListings: 400,
                name: 'Prestige',
                peekWords: -1,
                platform: 'snagshout',
                price: 0,
                pricePerBoost: 19,
                pricePerBotRebate: 1,
                pricePerRebate: 1.5,
                purchasable: true,
                rebates: true,
                reverseAsin: -1,
                videos: 100,
            }));
        }

        if (userPlan !== plan) {
            setPlan(userPlan);
        }
    }, [me]);

    return (
        r(Provider, { store }, [
            r(MuiThemeProvider, { muiTheme }, [
                r(MeContext.Provider, { value: [me, refreshMe, isFetchingMe] }, [
                    r(PlanContext.Provider, {
                        value: [plan, setPlan, plans, isFetchingPlans, refreshPlans]
                    }, [
                        r(NotificationContext.Provider, {
                            value: [notificationCount, setNotificationCount],
                        }, [
                            r(Elements, {
                                options: elementsOptions,
                                stripe: stripePromise,
                            }, [
                                children,
                            ]),
                        ]),
                    ]),
                ]),
            ]),
        ])
    );
}

_ProviderWrapper.propTypes = propTypes;

export default function ProviderWrapper({ children, ...props }) {
    const [me, setMe] = useState(MeRecord());
    const [plans, setPlans] = useState(List());
    const [isFetchingMe, setIsFetchingMe] = useState(false);
    const [isFetchingPlans, setIsFetchingPlans] = useState(false);
    const embeds = [
        'company.activeSubscription',
        'company.rateLimits',
        'facebookAccount',
    ].join(',');
    const params = {
        embeds,
        withBalance: 1,
    };

    const getMe = () => {
        return new Promise((resolve, reject) => {
            setIsFetchingMe(true);

            return authHttp({
                method: 'get',
                params,
                url: '/me',
            }).then((res) => {
                setIsFetchingMe(false);
                resolve(mapMe(res.data));
            }).catch((e) => {
                setIsFetchingMe(false);
                reject(e);
            });
        });
    };

    const getPlans = () => {
        return new Promise((resolve, reject) => {
            setIsFetchingPlans(true);
            http({
                method: 'get',
                url: '/plans?platform=massview',
            }).then((res) => {
                setIsFetchingPlans(false);
                resolve(fromJS(res.data));
            }).catch((e) => {
                setIsFetchingPlans(false);
                reject(e);
            });
        });
    };

    useEffect(() => {
        Promise.all([getMe(), getPlans()]).then((res) => {
            setMe(res[0]);
            setPlans(res[1]);
        });
    }, []);

    function fetchMe() {
        return authHttp.get('/me', { params }).then((res) => {
            const meResponse = mapMe(res.data);
            setMe(meResponse);
            setIsFetchingMe(false);

            return meResponse;
        }).catch(() => {
            setIsFetchingMe(false);
        });
    }

    return r(_ProviderWrapper, {
        isFetchingMe,
        isFetchingPlans,
        me,
        plans,
        refreshMe: fetchMe,
        refreshPlans: getPlans,
        ...props,
    }, [children]);
}
